import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BatteryCharging,
  Zap,
  Sun,
  ShieldCheck,
  Wallet,
  Moon,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Sparkles,
  Clock,
  PhoneCall,
  TrendingDown,
} from 'lucide-react';
import { LOGO_URL } from '../data/mock';
import { LEGAL_ENTITY } from '../components/Footer';
import { ExpertBadge } from '../components/ExpertBadge';

// EOFY offer ends 5pm AEST, 30 June 2026 => 07:00 UTC
const DEADLINE_UTC = new Date('2026-06-30T07:00:00Z');
const ENQUIRE_LINK = '/compare?product=battery';

const useCountdown = (target) => {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      expired: false,
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return time;
};

const CountdownUnit = ({ value, label, testid }) => (
  <div
    className="flex flex-col items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-3 py-3 sm:px-5 sm:py-4 min-w-[64px] sm:min-w-[84px]"
    data-testid={testid}
  >
    <span className="text-2xl sm:text-4xl font-black tabular-nums leading-none text-white">
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-1.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold text-orange-200">
      {label}
    </span>
  </div>
);

const PRICE_TIERS = [
  {
    size: '10 kWh',
    price: '3,999',
    tagline: 'Great for everyday homes',
    blurb: 'Store your solar and power your evenings.',
    featured: false,
    testid: 'battery-tier-small',
  },
  {
    size: '20 kWh',
    price: '4,999',
    tagline: 'Best value per kWh',
    blurb: 'More storage for higher evening usage & backup.',
    featured: true,
    testid: 'battery-tier-large',
  },
];

const BatteryDealPage = () => {
  const { days, hours, minutes, seconds, expired } = useCountdown(DEADLINE_UTC);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="battery-deal-page">
      {/* ============ Slim Header ============ */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="bd-logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CompareYourOptions.com.au" className="h-8 w-auto" />
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-cyo-orange bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            EOFY Battery Deal
          </span>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gray-950 text-white" data-testid="bd-hero">
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-14 sm:pt-16 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* LEFT: copy */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-400/30 text-orange-200 px-4 py-1.5 rounded-full">
                <Zap className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-bold tracking-wide">
                  Limited EOFY offer — ends 30 June 2026
                </span>
              </div>

              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
                A home battery
                <br />
                <span className="text-cyo-orange">from $3,999</span>
                <br className="hidden sm:block" /> this EOFY
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Store your solar, slash your evening power bills and keep the lights on
                during outages. Premium battery storage at one of the sharpest prices of the
                year — but only until end of financial year.
              </p>

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to={ENQUIRE_LINK}
                  data-testid="bd-hero-cta"
                  className="inline-flex items-center justify-center gap-2 bg-cyo-orange hover:bg-orange-600 text-white font-bold px-7 py-4 rounded-full shadow-lg shadow-orange-900/30 hover:-translate-y-0.5 transition-all"
                >
                  Enquire About This Deal
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <p className="mt-5 text-xs text-gray-400 flex items-center justify-center lg:justify-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                100% free, no-obligation enquiry • We connect you with a trusted provider
              </p>
              <div className="mt-6 flex justify-center lg:justify-start">
                <ExpertBadge variant="dark" align="left" />
              </div>
            </div>

            {/* RIGHT: deal card */}
            <div className="lg:col-span-5">
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-white/10 rounded-[2rem] p-6 sm:p-8 shadow-2xl">
                <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 bg-cyo-orange/20 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-cyo-orange/15 text-cyo-orange flex items-center justify-center">
                    <BatteryCharging className="w-7 h-7" />
                  </div>
                  <p className="mt-5 text-xs font-bold uppercase tracking-widest text-orange-300">
                    EOFY battery package
                  </p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-5xl sm:text-6xl font-black tabular-nums leading-none">
                      $3,999
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-300">
                    from price for a <span className="font-semibold text-white">10 kWh</span>{' '}
                    system — or step up to{' '}
                    <span className="font-semibold text-white">20 kWh from $4,999</span>.
                  </p>

                  {/* Countdown */}
                  <div className="mt-6">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-orange-300 flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" />
                      {expired ? 'Offer closed' : 'Offer ends in'}
                    </p>
                    {!expired ? (
                      <div className="mt-3 flex items-center gap-2 sm:gap-2.5">
                        <CountdownUnit value={days} label="Days" testid="bd-cd-days" />
                        <CountdownUnit value={hours} label="Hrs" testid="bd-cd-hours" />
                        <CountdownUnit value={minutes} label="Min" testid="bd-cd-mins" />
                        <CountdownUnit value={seconds} label="Sec" testid="bd-cd-secs" />
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-gray-300">
                        Enquire to check current battery offers.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICE TIERS ============ */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20" data-testid="bd-tiers">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <Wallet className="w-3.5 h-3.5" />
            Pick your size
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            Two sizes. One sharp <span className="text-cyo-orange">EOFY price.</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Both packages include a hybrid inverter and standard installation. Tell us a
            little about your home and we&apos;ll confirm which size suits you.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
          {PRICE_TIERS.map((t) => (
            <div
              key={t.size}
              data-testid={t.testid}
              className={`relative rounded-3xl p-6 sm:p-8 shadow-sm transition-all hover:shadow-lg ${
                t.featured
                  ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white border border-white/10'
                  : 'bg-white border border-gray-200'
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-cyo-orange px-3 py-1 rounded-full">
                  Most popular
                </span>
              )}
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                  t.featured ? 'bg-cyo-orange/20 text-orange-300' : 'bg-blue-50 text-blue-600'
                }`}
              >
                <BatteryCharging className="w-6 h-6" />
              </div>
              <p
                className={`text-xs font-bold uppercase tracking-widest ${
                  t.featured ? 'text-orange-300' : 'text-blue-600'
                }`}
              >
                {t.size} battery
              </p>
              <div className="mt-2 flex items-end gap-1.5">
                <span className={`text-sm font-semibold ${t.featured ? 'text-gray-300' : 'text-gray-500'}`}>
                  from
                </span>
                <span className="text-4xl sm:text-5xl font-black tabular-nums leading-none">
                  ${t.price}
                </span>
              </div>
              <p className={`mt-1 text-xs font-bold ${t.featured ? 'text-orange-200' : 'text-cyo-orange'}`}>
                {t.tagline}
              </p>
              <p className={`mt-3 text-sm leading-relaxed ${t.featured ? 'text-gray-300' : 'text-gray-600'}`}>
                {t.blurb}
              </p>
              <Link
                to={ENQUIRE_LINK}
                data-testid={`${t.testid}-cta`}
                className={`mt-6 inline-flex items-center justify-center gap-2 w-full font-bold px-5 py-3 rounded-full transition-all ${
                  t.featured
                    ? 'bg-white text-gray-900 hover:bg-gray-100'
                    : 'bg-cyo-orange text-white hover:bg-orange-600'
                }`}
              >
                Enquire on this size
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[11px] text-gray-500 leading-relaxed text-center max-w-2xl mx-auto">
          Prices shown are indicative &quot;from&quot; prices for a standard single-phase
          installation and assume the applicable Battery STC rebate is assigned to the
          installer. Final pricing is confirmed after a site inspection. See full terms
          below.
        </p>
      </section>

      {/* ============ WHY A BATTERY ============ */}
      <section
        className="relative bg-gradient-to-b from-orange-50/60 via-white to-blue-50/30 py-14 sm:py-20"
        data-testid="bd-why"
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Zap className="w-3.5 h-3.5" />
              Why add a battery
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              Get more from every
              <br className="hidden sm:block" /> kilowatt you generate
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: TrendingDown,
                tone: 'orange',
                title: 'Lower evening bills',
                body: 'Use your stored solar at night instead of buying pricey grid power.',
              },
              {
                icon: Sun,
                tone: 'blue',
                title: 'Store your solar',
                body: 'Stop exporting cheaply by day — keep your own energy for later.',
              },
              {
                icon: Moon,
                tone: 'orange',
                title: 'Blackout backup',
                body: 'Keep essentials running when the grid goes down.*',
              },
              {
                icon: Wallet,
                tone: 'blue',
                title: 'EOFY value',
                body: 'One of the sharpest battery prices of the year — for a limited time.',
              },
            ].map(({ icon: Icon, tone, title, body }) => (
              <div
                key={title}
                className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg transition-shadow"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    tone === 'orange' ? 'bg-orange-50 text-cyo-orange' : 'bg-blue-50 text-blue-600'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-gray-900 leading-snug">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-[11px] text-gray-500 text-center max-w-2xl mx-auto">
            *Backup capability depends on your system configuration and is confirmed during
            your consultation.
          </p>
        </div>
      </section>

      {/* ============ WHATS INCLUDED (brief) ============ */}
      <section className="max-w-4xl mx-auto px-4 py-14 sm:py-20" data-testid="bd-included">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            What&apos;s in the package
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            The essentials are covered. Your consultant walks you through the finer detail
            when you enquire.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {[
            'Hybrid inverter + battery system',
            'Smart meter supply & installation',
            'Standard delivery & installation',
            'Battery STC rebate handled for you',
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 bg-white border border-gray-100 shadow-sm rounded-2xl px-4 py-3.5"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-800">{item}</span>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-gray-600 max-w-xl mx-auto">
          Want the full breakdown, exact inclusions and your tailored price? That&apos;s a
          quick enquiry away.
        </p>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        className="relative bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-14 sm:py-20 overflow-hidden"
        data-testid="bd-final-cta"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-10 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <BatteryCharging className="w-10 h-10 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            EOFY pricing ends 30 June 2026.
            <br />
            <span className="text-white">Lock in your interest today.</span>
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto text-sm sm:text-base">
            Enquire now and we&apos;ll connect you with a trusted provider who will confirm
            your size, your exact price and the next steps. Free, no obligation.
          </p>

          {!expired && (
            <div className="mt-7 inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-2 text-sm font-bold">
              <Clock className="w-4 h-4" />
              {days} {days === 1 ? 'day' : 'days'}, {hours} {hours === 1 ? 'hr' : 'hrs'} left
            </div>
          )}

          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={ENQUIRE_LINK}
              data-testid="bd-final-cta-enquire"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-7 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Enquire About This Deal
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${LEGAL_ENTITY.phone || ''}`}
              data-testid="bd-final-cta-phone"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white font-bold px-7 py-4 rounded-full hover:bg-white/20 transition-all"
            >
              <PhoneCall className="w-4 h-4" />
              Speak to our team
            </a>
          </div>
        </div>
      </section>

      {/* ============ FOOTER + DISCLAIMERS ============ */}
      <footer className="bg-gray-950 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link to="/" className="inline-block">
            <img
              src={LOGO_URL}
              alt={LEGAL_ENTITY.website}
              className="h-8 w-auto mx-auto brightness-0 invert opacity-90"
            />
          </Link>
          <p className="mt-4 text-xs sm:text-sm">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN{' '}
            {LEGAL_ENTITY.abn}).
          </p>

          <div className="mt-5 max-w-2xl mx-auto text-left bg-white/5 border border-white/10 rounded-2xl p-5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-300 text-center mb-3">
              Important information
            </p>
            <ul className="space-y-2 text-[11px] leading-relaxed text-gray-500 list-disc pl-4">
              <li>
                This battery offer is supplied and installed by a trusted third-party
                provider. CompareYourOptions.com.au connects interested customers with a
                trusted provider and does not itself supply or install the system.
              </li>
              <li>
                Prices shown are indicative &quot;from&quot; prices only, based on a standard
                single-phase installation, and assume the applicable Battery STC rebate is
                assigned to the installer.
              </li>
              <li>
                Additional costs may apply depending on site conditions, switchboard
                requirements, battery location, cable run, wall surface, base/slab condition,
                access and safety requirements. Final pricing is confirmed before
                installation is organised.
              </li>
              <li>
                A site inspection deposit may be required to confirm your order and progress
                the installation. Offer available while the EOFY promotion runs and ends 30
                June 2026. Provider terms apply and may change.
              </li>
            </ul>
          </div>

          <div className="mt-5 flex justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-white">
              Privacy
            </Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white">
              Terms
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-white">
              About
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-gray-600">
            © {new Date().getFullYear()} {LEGAL_ENTITY.website}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BatteryDealPage;
